import { FC, useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { TaskItem } from '@demind-inc/core';
import dayjs, { Dayjs } from 'dayjs';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { ExpandMore, Add } from '@mui/icons-material';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useDrop } from 'react-dnd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import './TasksAccordion.scss';
import { TaskCard } from '../TaskCard';
import { selectedDateAtom, generalSnackBarAtom } from '../../../data-access';
import { TaskQuickAddCard } from './TaskQuickAddCard';
import { getDueDateYYYYMMDD, calculateNewSortIndex } from '../../../helpers';
import { isAppleRemindersDesktopSupported } from '../../../helpers/appleRemindersDesktop.helpers';
import { formatDataDate } from '../../DataView/i18n';

const TASK_CARD_CLASS = 'task-card';
const AUTO_EXPAND_DELAY_MS = 600;

type SectionType = 'overdue' | 'backlog' | 'date';

interface TasksAccordionProps {
  date?: Dayjs;
  title?: string;
  sectionType?: SectionType;
  tasks: TaskItem[];
  loading?: boolean;
  defaultExpanded?: boolean;
  className?: string;
  onMoveTask?: (task: TaskItem, sortIndex: number) => void;
  onReorderTask?: (task: TaskItem, sortIndex: number) => void;
}

const TasksAccordion: FC<TasksAccordionProps> = ({
  date,
  title,
  sectionType = 'date',
  tasks,
  loading = false,
  defaultExpanded = false,
  className,
  onMoveTask,
  onReorderTask,
}) => {
  const { t, i18n } = useTranslation();
  const TASKS_PAGE_SIZE = 10;
  const selectedDate = useRecoilValue(selectedDateAtom);
  const setGeneralSnackbar = useSetRecoilState(generalSnackBarAtom);
  const [isQuickAdding, setIsQuickAdding] = useState(false);
  const [visibleTaskCount, setVisibleTaskCount] = useState(TASKS_PAGE_SIZE);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const listRef = useRef<HTMLDivElement>(null);
  const autoExpandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resolvedTitle = useMemo(() => {
    if (title) {
      return title;
    }

    if (!date) {
      return t('task.sections.tasks');
    }

    if (date.isSame(dayjs(), 'day')) {
      return t('task.sections.today');
    }

    if (date.isSame(dayjs().add(1, 'day'), 'day')) {
      return t('task.sections.tomorrow');
    }

    return formatDataDate(i18n.language, date.toDate(), {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
    });
  }, [date, i18n.language, title, t]);

  const isOverdue = sectionType === 'overdue';
  const sectionDate = date?.format('YYYY-MM-DD');

  const isReadOnlySection = isOverdue;

  const handleAddTask = useCallback(() => {
    setIsQuickAdding(true);
  }, []);

  const handleCancelQuickAdd = useCallback(() => {
    setIsQuickAdding(false);
  }, []);

  const quickAddDate = useMemo(() => {
    if (sectionType === 'backlog') {
      return undefined;
    }

    return (date ?? selectedDate ?? dayjs()).format('YYYY-MM-DD');
  }, [date, sectionType, selectedDate]);

  const visibleTasks = useMemo(() => {
    return tasks.slice(0, visibleTaskCount);
  }, [tasks, visibleTaskCount]);

  const hasMoreTasks = tasks.length > visibleTaskCount;
  const remainingTaskCount = Math.max(tasks.length - visibleTaskCount, 0);
  const nextLoadCount = Math.min(TASKS_PAGE_SIZE, remainingTaskCount);

  const handleShowMore = useCallback(() => {
    setVisibleTaskCount((prev) => prev + TASKS_PAGE_SIZE);
  }, []);

  const clearAutoExpandTimer = useCallback(() => {
    if (autoExpandTimerRef.current) {
      clearTimeout(autoExpandTimerRef.current);
      autoExpandTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return clearAutoExpandTimer;
  }, [clearAutoExpandTimer]);

  const mapToFullIndex = useCallback(
    (visualIndex: number, rendered: TaskItem[], full: TaskItem[]): number => {
      if (full.length === 0 || rendered.length === 0) return 0;

      if (visualIndex >= rendered.length) {
        const lastRendered = rendered[rendered.length - 1];
        const fullIdx = full.findIndex((t) => t.taskId === lastRendered.taskId);
        return fullIdx + 1;
      }

      const taskAtVisualIndex = rendered[visualIndex];
      return full.findIndex((t) => t.taskId === taskAtVisualIndex.taskId);
    },
    []
  );

  const [{ isOver, canDrop, draggedItem }, drop] = useDrop(
    () => ({
      accept: 'TASK',
      drop: (item: TaskItem, monitor) => {
        if (monitor.didDrop() || dragOverIndex === null) return;

        const isDraggedToOtherSection = getDueDateYYYYMMDD(item.dueDateTime) !== sectionDate;

        if (isDraggedToOtherSection && onMoveTask) {
          const fullIndex = mapToFullIndex(dragOverIndex, visibleTasks, tasks);
          const newSortIndex = calculateNewSortIndex(tasks, fullIndex);
          onMoveTask(item, newSortIndex);
        } else if (onReorderTask) {
          const allTasksWithoutDragged = tasks.filter((t) => t.taskId !== item.taskId);
          const visibleWithoutDragged = visibleTasks.filter((t) => t.taskId !== item.taskId);
          const originalFullIndex = tasks.findIndex((t) => t.taskId === item.taskId);
          const fullIndex = mapToFullIndex(
            dragOverIndex,
            visibleWithoutDragged,
            allTasksWithoutDragged
          );

          if (originalFullIndex !== -1 && fullIndex === originalFullIndex) {
            setDragOverIndex(null);
            return;
          }

          if (!item.completed) {
            const newSortIndex = calculateNewSortIndex(allTasksWithoutDragged, fullIndex);
            onReorderTask(item, newSortIndex);
          }
        }
        setDragOverIndex(null);
      },
      hover: (_item: TaskItem, monitor) => {
        if (sectionType === 'overdue' || sectionType === 'backlog') return;

        if (!expanded && monitor.isOver({ shallow: true })) {
          if (!autoExpandTimerRef.current) {
            autoExpandTimerRef.current = setTimeout(() => {
              setExpanded(true);
              autoExpandTimerRef.current = null;
            }, AUTO_EXPAND_DELAY_MS);
          }
        }

        if (!listRef.current) return;

        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;

        const taskElements = listRef.current.getElementsByClassName(TASK_CARD_CLASS);
        let newDragOverIndex = 0;
        let minDistance = Infinity;

        if (taskElements.length > 0) {
          const firstTaskRect = taskElements[0].getBoundingClientRect();
          const distanceToFirst = Math.abs(firstTaskRect.top - clientOffset.y);
          minDistance = distanceToFirst;
          newDragOverIndex = 0;
        }

        for (let i = 0; i < taskElements.length; i++) {
          const taskRect = taskElements[i].getBoundingClientRect();
          const taskMiddleY = taskRect.top + (taskRect.bottom - taskRect.top) / 2;
          const distance = Math.abs(taskMiddleY - clientOffset.y);

          if (distance < minDistance) {
            minDistance = distance;
            newDragOverIndex = i + 1;
          }
        }

        if (taskElements.length > 0) {
          const lastTaskRect = taskElements[taskElements.length - 1].getBoundingClientRect();
          const distanceToLast = Math.abs(lastTaskRect.bottom - clientOffset.y);
          if (distanceToLast < minDistance) {
            newDragOverIndex = taskElements.length;
          }
        }

        if (newDragOverIndex !== dragOverIndex) {
          setDragOverIndex(newDragOverIndex);
        }
      },
      canDrop: (item: TaskItem) => {
        if (item.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported()) {
          return false;
        }

        if (sectionType === 'overdue') return false;
        if (sectionType === 'backlog') return false;

        return true;
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
        draggedItem: monitor.getItem() as TaskItem | null,
      }),
    }),
    [
      onMoveTask,
      onReorderTask,
      dragOverIndex,
      visibleTasks,
      tasks,
      mapToFullIndex,
      sectionDate,
      sectionType,
      expanded,
    ]
  );

  const hasShownInvalidToastRef = useRef(false);

  useEffect(() => {
    if (!isOver) {
      clearAutoExpandTimer();
      setDragOverIndex(null);
      hasShownInvalidToastRef.current = false;
      return;
    }

    if (isOver && !canDrop && draggedItem && !hasShownInvalidToastRef.current) {
      hasShownInvalidToastRef.current = true;

      if (sectionType === 'overdue') {
        setGeneralSnackbar(t('task.drag.cannotMoveToOverdue'));
      } else if (sectionType === 'backlog') {
        setGeneralSnackbar(t('task.drag.cannotMoveToBacklog'));
      }
    }
  }, [isOver, canDrop, draggedItem, clearAutoExpandTimer, sectionType, setGeneralSnackbar, t]);

  const showGhostCard = isOver && canDrop && dragOverIndex !== null && !!draggedItem;

  const renderTasks = useMemo(() => {
    if (!isOver || !canDrop || !draggedItem) return visibleTasks;
    return visibleTasks.filter((t) => t.taskId !== draggedItem.taskId);
  }, [visibleTasks, isOver, canDrop, draggedItem]);

  return (
    <Accordion
      ref={drop}
      className={clsx('tasks-accordion', className, {
        'tasks-accordion--drop-target': isOver && canDrop,
        'tasks-accordion--invalid-target': isOver && !canDrop,
      })}
      elevation={0}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMore className="tasks-accordion__expand-icon" />}
        className="tasks-accordion__summary"
      >
        <div className="tasks-accordion__summary__left">
          <Typography variant="body1" className="tasks-accordion__title">
            {resolvedTitle}
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails className="tasks-accordion__details">
        <div ref={listRef} className="tasks-accordion__task-list">
          {tasks.length ? (
            renderTasks.length > 0 ? (
              renderTasks.map((task, idx) => (
                <div key={task.taskId} className="tasks-accordion__task-list__item">
                  {showGhostCard && dragOverIndex === idx && (
                    <div className="tasks-accordion__ghost-card">
                      <TaskCard task={draggedItem} />
                    </div>
                  )}
                  <TaskCard task={task} draggable />
                  {idx !== renderTasks.length - 1 && !showGhostCard && <Divider />}
                  {idx !== renderTasks.length - 1 && showGhostCard && dragOverIndex !== idx + 1 && (
                    <Divider />
                  )}
                </div>
              ))
            ) : (
              showGhostCard && (
                <div className="tasks-accordion__ghost-card" style={{ width: '100%' }}>
                  <TaskCard task={draggedItem} />
                </div>
              )
            )
          ) : (
            <div className="tasks-accordion__placeholder">
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  {showGhostCard && (
                    <div className="tasks-accordion__ghost-card" style={{ width: '100%' }}>
                      <TaskCard task={draggedItem} />
                    </div>
                  )}
                  {!showGhostCard && (
                    <Typography variant="body2">{t('task.sections.noTasks')}</Typography>
                  )}
                </>
              )}
            </div>
          )}
          {showGhostCard &&
            dragOverIndex !== null &&
            dragOverIndex >= renderTasks.length &&
            renderTasks.length > 0 && (
              <div className="tasks-accordion__ghost-card">
                <TaskCard task={draggedItem} />
              </div>
            )}
        </div>
        {hasMoreTasks && (
          <Button className="tasks-accordion__show-more-button" onClick={handleShowMore}>
            <Typography variant="body2">
              {t('task.sections.showMore', { count: nextLoadCount })}
            </Typography>
          </Button>
        )}
        {isQuickAdding && !isReadOnlySection && (
          <TaskQuickAddCard date={quickAddDate} tasks={tasks} onCancel={handleCancelQuickAdd} />
        )}
        {!isReadOnlySection && (
          <Button
            className="tasks-accordion__add-task-button"
            data-testid="add-task-button"
            onClick={handleAddTask}
            disabled={isQuickAdding}
          >
            <Add className="tasks-accordion__add-task-button__icon" />
            <Typography variant="body1" className="tasks-accordion__add-task-button__text">
              {t('task.sections.addTask')}
            </Typography>
          </Button>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default TasksAccordion;
